import React, { useEffect, useState } from 'react';
import {
  evaluationPercentage,
  percentageLabel,
  percentageDelta,
} from '../../lib/evaluationIntegrity';
import { formatAnswerForPDF } from '../../lib/answerFormat';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI, evaluationsAPI } from '../../api/client';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import SemaforoBadge from '../../components/SemaforoBadge';
import { VersionTrendChart, SectionBarChart } from '../../components/ScoreChart';
import { getEvalSections, getSectionStatsFromEval } from '../../lib/evalSections';

function DeltaBadge({ delta }) {
  if (delta > 0) return (
    <span className="flex items-center gap-1 text-green-700 font-bold text-sm">
      <TrendingUp size={14} /> +{delta}
    </span>
  );
  if (delta < 0) return (
    <span className="flex items-center gap-1 text-red-600 font-bold text-sm">
      <TrendingDown size={14} /> {delta}
    </span>
  );
  return <span className="flex items-center gap-1 text-slate-400 text-sm"><Minus size={14} /> 0</span>;
}

export default function EvaluationCompare() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [vA, setVA] = useState('');
  const [vB, setVB] = useState('');
  const [evalA, setEvalA] = useState(null);
  const [evalB, setEvalB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [, evals] = await Promise.all([
          projectsAPI.get(projectId),
          evaluationsAPI.list(),
        ]);
        const projectEvals = (evals.data.items || [])
          .filter(e => e.project_id === projectId && e.status === 'publicada')
          .sort((a, b) => b.version - a.version);
        setEvaluations(projectEvals);
        if (projectEvals.length >= 2) {
          setVA(projectEvals[1].id);
          setVB(projectEvals[0].id);
        } else if (projectEvals.length === 1) {
          setVA(projectEvals[0].id);
        }
      } catch { toast.error('Error al cargar evaluaciones'); }
      finally { setLoading(false); }
    }
    load();
  }, [projectId]);

  useEffect(() => {
    if (!vA && !vB) return;
    setLoadingDetail(true);
    const promises = [];
    if (vA) promises.push(evaluationsAPI.get(vA));
    if (vB) promises.push(evaluationsAPI.get(vB));
    Promise.all(promises).then(results => {
      if (vA) setEvalA(results[0]?.data || null);
      if (vB) setEvalB(results[vA ? 1 : 0]?.data || null);
    }).catch(() => toast.error('Error al cargar detalles')).finally(() => setLoadingDetail(false));
  }, [vA, vB]);

  // #TemplateEvalRender — antes esta pantalla buscaba cada respuesta con
  // `items.find(i => i.criterio_id === criterioId)` y recorría el catálogo
  // legacy de 35 criterios. En evaluaciones de plantilla ningún lookup
  // casaba: la tabla "Comparación por Criterio" salía COMPLETAMENTE VACÍA
  // (todas las filas se descartaban por scoreA y scoreB en null) y las dos
  // gráficas por sección mostraban 0 contra un máximo legacy.
  // Ahora la comparación se arma con las secciones reales de cada versión.
  const sectionStatsA = evalA ? getSectionStatsFromEval(evalA) : null;
  const sectionStatsB = evalB ? getSectionStatsFromEval(evalB) : null;

  const comparacion = React.useMemo(() => {
    if (!evalA || !evalB) return [];
    const secsA = getEvalSections(evalA);
    const secsB = getEvalSections(evalB);
    const puntaje = (row) =>
      row?.item?.score_set && !row.item.not_applicable ? row.item.score : null;
    // #Fase1-10 — Las versiones conservan ids; nunca se mezclan secciones por nombre.
    const porClave = new Map(
      secsB.flatMap((section) => section.rows).map((row) => [String(row.key), row]));

    const nombres = [...new Set([...secsA.map((s) => s.key), ...secsB.map((s) => s.key)])];
    return nombres.map((key) => {
      const a = secsA.find((s) => s.key === key);
      const b = secsB.find((s) => s.key === key);
      const casadas = new Set();
      const rows = (a?.rows || []).map(r => {
        const rb = porClave.get(String(r.key));
        if (rb) casadas.add(String(rb.key));
        return { key: r.key, number: r.number, criterion: r.criterion, scoreA: puntaje(r), scoreB: puntaje(rb),
          answerA: formatAnswerForPDF(r.criterion, r.item),
          answerB: rb ? formatAnswerForPDF(rb.criterion, rb.item) : '—',
        };
      });
      // Criterios que solo existen en la versión B.
      (b?.rows || []).forEach(r => {
        if (casadas.has(String(r.key))) return;
        rows.push({ key: `b-${r.key}`, number: r.number, criterion: r.criterion, scoreA: null, scoreB: puntaje(r),
          answerA: '—',
          answerB: formatAnswerForPDF(r.criterion, r.item),
        });
      });
      return { key, name: a?.name || b?.name, rows };
    });
  }, [evalA, evalB]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;

  if (evaluations.length < 2) return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/dashboard/proyectos/${projectId}`)} className="soem-btn-secondary p-2" aria-label="Volver al proyecto"><ArrowLeft size={18} /></button>
        <h1 className="text-2xl font-bold text-soem-navy">Comparar Versiones</h1>
      </div>
      <div className="soem-card p-12 text-center">
        <p className="text-slate-500">Se necesitan al menos <strong>2 evaluaciones publicadas</strong> para comparar.</p>
        <p className="text-slate-400 text-sm mt-2">Actualmente hay {evaluations.length} evaluación(es) publicada(s).</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="evaluation-compare">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/dashboard/proyectos/${projectId}`)} className="soem-btn-secondary p-2" aria-label="Volver al proyecto"><ArrowLeft size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Comparar Versiones</h1>
          <p className="text-slate-500 text-sm mt-0.5">{evaluations.length} versiones publicadas disponibles</p>
        </div>
      </div>

      {/* Version selectors */}
      <div className="soem-card p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="f-versión-a-base" className="soem-label block mb-2">Versión A (Base)</label>
            <select id="f-versión-a-base" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={vA} onChange={e => setVA(e.target.value)} data-testid="version-a-select">
              <option value="">— Seleccionar —</option>
              {evaluations.map(e => (
                <option key={e.id} value={e.id}>
                  {e.general_fields?.instalacion || 'Evaluación'} v{e.version}
                  {' '}({percentageLabel(evaluationPercentage(e))})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="f-versión-b-comparar-con" className="soem-label block mb-2">Versión B (Comparar con)</label>
            <select id="f-versión-b-comparar-con" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={vB} onChange={e => setVB(e.target.value)} data-testid="version-b-select">
              <option value="">— Seleccionar —</option>
              {evaluations.map(e => (
                <option key={e.id} value={e.id}>
                  {e.general_fields?.instalacion || 'Evaluación'} v{e.version}
                  {' '}({percentageLabel(evaluationPercentage(e))})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loadingDetail && (
        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>
      )}

      {evalA && evalB && !loadingDetail && (
        <>
          {/* Summary comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Version A */}
            <div className="soem-card p-5 border-t-4 border-t-slate-400">
              <p className="soem-label mb-2">Versión A — v{evalA.version}</p>
              <p className="text-2xl font-black text-soem-navy">{evalA.score}/{evalA.max_score}</p>
              <p className="text-slate-400 text-sm mb-3">
                {percentageLabel(evaluationPercentage(evalA))}
              </p>
              <SemaforoBadge semaforo={evalA.semaforo} percentage={evaluationPercentage(evalA)} size="sm" />
            </div>

            {/* Delta */}
            <div className="soem-card p-5 flex flex-col items-center justify-center text-center">
              <p className="soem-label mb-3">Diferencia</p>
              <div className={`text-4xl font-black ${
                evaluationPercentage(evalA) == null || evaluationPercentage(evalB) == null
                  ? 'text-slate-400'
                  : evalB.score > evalA.score ? 'text-green-600'
                    : evalB.score < evalA.score ? 'text-red-600' : 'text-slate-400'
              }`}>
                {evaluationPercentage(evalA) == null || evaluationPercentage(evalB) == null
                  ? '—'
                  : evalB.score - evalA.score > 0 ? '+' : ''}
                {evaluationPercentage(evalA) == null || evaluationPercentage(evalB) == null
                  ? ''
                  : evalB.score - evalA.score}
              </div>
              <p className="text-slate-500 text-sm mt-1">
                pts ({percentageDelta(evaluationPercentage(evalB), evaluationPercentage(evalA)) ?? '—'})
              </p>
              {evalB.semaforo !== evalA.semaforo && (
                <div className="flex items-center gap-2 mt-3">
                  <SemaforoBadge semaforo={evalA.semaforo}
                    percentage={evaluationPercentage(evalA)}
                    size="sm" />
                  <ArrowRight size={14} className="text-slate-400" />
                  <SemaforoBadge semaforo={evalB.semaforo}
                    percentage={evaluationPercentage(evalB)} size="sm" />
                </div>
              )}
            </div>

            {/* Version B */}
            <div className="soem-card p-5 border-t-4 border-t-soem-primary">
              <p className="soem-label mb-2">Versión B — v{evalB.version}</p>
              <p className="text-2xl font-black text-soem-navy">{evalB.score}/{evalB.max_score}</p>
              <p className="text-slate-400 text-sm mb-3">
                {percentageLabel(evaluationPercentage(evalB))}
              </p>
              <SemaforoBadge semaforo={evalB.semaforo} percentage={evaluationPercentage(evalB)} size="sm" />
            </div>
          </div>

          {/* Section comparison charts */}
          {sectionStatsA && sectionStatsB && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="soem-card p-5">
                <p className="font-bold text-soem-navy mb-3">v{evalA.version} — Por Sección</p>
                <SectionBarChart sectionStats={sectionStatsA} />
              </div>
              <div className="soem-card p-5">
                <p className="font-bold text-soem-navy mb-3">v{evalB.version} — Por Sección</p>
                <SectionBarChart sectionStats={sectionStatsB} />
              </div>
            </div>
          )}

          {/* Trend across all versions */}
          <div className="soem-card p-5">
            <p className="font-bold text-soem-navy mb-3">Evolución del Puntaje</p>
            <VersionTrendChart evaluations={evaluations} />
          </div>

          {/* Criteria comparison table */}
          <div className="soem-card overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="font-bold text-soem-navy">Comparación por Criterio</h2>
              <p className="text-slate-500 text-sm mt-0.5">
                <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold mr-3"><TrendingUp size={12} /> Mejora</span>
                <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold mr-3"><TrendingDown size={12} /> Retroceso</span>
                <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-semibold"><Minus size={12} /> Sin cambio</span>
              </p>
            </div>
            {comparacion.map(section => {
              const filas = section.rows;
              if (filas.length === 0) return null;
              return (
                <div key={section.key}>
                  <div className="bg-soem-navy px-5 py-2 flex justify-between items-center">
                    <span className="text-white font-bold text-xs uppercase tracking-wider">{section.name}</span>
                    <div className="flex gap-8 text-slate-400 text-xs font-mono">
                      <span>v{evalA.version}</span>
                      <span>v{evalB.version}</span>
                      <span>Δ</span>
                    </div>
                  </div>
                  {filas.map(r => {
                    const delta = percentageDelta(r.scoreB, r.scoreA);
                    const rowBg = delta > 0 ? 'bg-green-50' : '';
                    return (
                      <div key={r.key} className={`flex items-center justify-between px-5 py-3 border-b border-slate-50 ${rowBg}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="w-5 text-xs text-slate-400 font-mono flex-shrink-0">{r.number}</span>
                          <span className="text-sm text-soem-navy font-medium truncate">{r.criterion.title}</span>
                          {r.criterion.blocker && <span className="bg-red-100 text-red-700 text-xs px-1.5 rounded font-bold flex-shrink-0">B</span>}
                        </div>
                        <div className="flex items-center gap-6 flex-shrink-0 ml-4">
                          <span className="text-xs">{r.answerA}</span>
                          <span className="text-xs">{r.answerB}</span>
                          <div className="w-12 text-right">
                            {delta == null ? '—' : <DeltaBadge delta={delta} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
